import {
  faArrowsRotate,
  faBook,
  faBrain,
  faCircleQuestion,
  faComments,
  faCompass,
  faDoorOpen,
  faDumbbell,
  faEarListen,
  faEye,
  faFaceSmile,
  faFingerprint,
  faFire,
  faGraduationCap,
  faHandHoldingHeart,
  faHandshake,
  faHandsPraying,
  faHeart,
  faLightbulb,
  faMagnifyingGlass,
  faMicrophone,
  faMountain,
  faSun,
  faWater,
} from '@fortawesome/free-solid-svg-icons'

import type { LearnerFeedbackAttributeTagDisplayData } from '@/client/services/learnerFeedback/types'

export const LEARNER_FEEDBACK_TYPE_TUTOR_TO_LEARNER = 'tutor-to-learner'
export const LEARNER_FEEDBACK_TYPE_DIALOGUES = 'dialogues'

export const LEARNER_FEEDBACK_TYPES = [
  LEARNER_FEEDBACK_TYPE_TUTOR_TO_LEARNER,
  LEARNER_FEEDBACK_TYPE_DIALOGUES,
] as const

// never remove tags from here since we will be swapping them out and we
// always want learners to be able to see their data
export enum LearnerFeedbackAttributeTag {
  Friendly = 'friendly',
  Hardworking = 'hardworking',
  Supportive = 'supportive',
  Curious = 'curious',
  Persistent = 'persistent',
  Focused = 'focused',
  Respectful = 'respectful',
  Empathetic = 'empathetic',
  DialoguesCurious = 'dialogues-curious',
  Thoughtful = 'thoughtful',
  OpenMinded = 'open-minded',
  FindingCommonGround = 'finding-common-ground',
  ComfortExploringDisagreement = 'comfort-exploring-disagreement',
  ChallengedTheirOwnViews = 'challenged-their-own-views',
  AskedGoodQuestions = 'asked-good-questions',
  HelpedGuideDiscussion = 'helped-guide-discussion',
  NuancedPerspective = 'nuanced-perspective',
  GoodListener = 'good-listener',
  Knowledgeable = 'knowledgeable',
  WellSpoken = 'well-spoken',
  Logical = 'logical',
  Positive = 'positive',
  Passionate = 'passionate',
  Authentic = 'authentic',
  Calm = 'calm',
}

// here is where you can remove tags that aren't currently in use
export const CURRENT_ATTRIBUTE_TAGS_ORDERED_VALUES = [
  LearnerFeedbackAttributeTag.Focused,
  LearnerFeedbackAttributeTag.Curious,
  LearnerFeedbackAttributeTag.Friendly,
  LearnerFeedbackAttributeTag.Hardworking,
  LearnerFeedbackAttributeTag.Persistent,
  LearnerFeedbackAttributeTag.Supportive,
]

export const CURRENT_DIALOGUES_PEER_ATTRIBUTE_TAGS_ORDERED_VALUES: LearnerFeedbackAttributeTag[] = [
  LearnerFeedbackAttributeTag.Respectful,
  LearnerFeedbackAttributeTag.Empathetic,
  LearnerFeedbackAttributeTag.DialoguesCurious,
  LearnerFeedbackAttributeTag.Thoughtful,
  LearnerFeedbackAttributeTag.OpenMinded,
  LearnerFeedbackAttributeTag.FindingCommonGround,
  LearnerFeedbackAttributeTag.ComfortExploringDisagreement,
  LearnerFeedbackAttributeTag.ChallengedTheirOwnViews,
  LearnerFeedbackAttributeTag.AskedGoodQuestions,
  LearnerFeedbackAttributeTag.HelpedGuideDiscussion,
  LearnerFeedbackAttributeTag.NuancedPerspective,
  LearnerFeedbackAttributeTag.GoodListener,
  LearnerFeedbackAttributeTag.Knowledgeable,
  LearnerFeedbackAttributeTag.WellSpoken,
  LearnerFeedbackAttributeTag.Logical,
  LearnerFeedbackAttributeTag.Positive,
  LearnerFeedbackAttributeTag.Passionate,
  LearnerFeedbackAttributeTag.Authentic,
  LearnerFeedbackAttributeTag.Calm,
]

export const ATTRIBUTE_TAG_DATA: Record<
  LearnerFeedbackAttributeTag,
  LearnerFeedbackAttributeTagDisplayData
> = {
  [LearnerFeedbackAttributeTag.Focused]: {
    label: 'Focused',
    icon: faMagnifyingGlass,
    color: 'purple.solid',
  },
  [LearnerFeedbackAttributeTag.Curious]: {
    label: 'Curious',
    icon: faBook,
    color: 'red.solid',
  },
  [LearnerFeedbackAttributeTag.Friendly]: {
    label: 'Friendly',
    icon: faFaceSmile,
    color: 'green.solid',
  },
  [LearnerFeedbackAttributeTag.Hardworking]: {
    label: 'Hardworking',
    icon: faDumbbell,
    color: 'orange.solid',
  },
  [LearnerFeedbackAttributeTag.Persistent]: {
    label: 'Persistent',
    icon: faMountain,
    color: 'blue.solid',
  },
  [LearnerFeedbackAttributeTag.Supportive]: {
    label: 'Supportive',
    icon: faHandHoldingHeart,
    color: 'yellow.solid',
  },
  // Dialogues tags
  [LearnerFeedbackAttributeTag.Respectful]: {
    label: 'Respectful',
    icon: faHandsPraying,
    color: 'blue.solid',
  },
  [LearnerFeedbackAttributeTag.Empathetic]: {
    label: 'Empathetic',
    icon: faHeart,
    color: 'blue.solid',
  },
  [LearnerFeedbackAttributeTag.DialoguesCurious]: {
    label: 'Curious',
    icon: faMagnifyingGlass,
    color: 'blue.solid',
  },
  [LearnerFeedbackAttributeTag.Thoughtful]: {
    label: 'Thoughtful',
    icon: faLightbulb,
    color: 'blue.solid',
  },
  [LearnerFeedbackAttributeTag.OpenMinded]: {
    label: 'Open minded',
    icon: faDoorOpen,
    color: 'blue.solid',
  },
  [LearnerFeedbackAttributeTag.FindingCommonGround]: {
    label: 'Finding common ground',
    icon: faHandshake,
    color: 'blue.solid',
  },
  [LearnerFeedbackAttributeTag.ComfortExploringDisagreement]: {
    label: 'Comfort exploring disagreement',
    icon: faComments,
    color: 'blue.solid',
  },
  [LearnerFeedbackAttributeTag.ChallengedTheirOwnViews]: {
    label: 'Challenged their own views',
    icon: faArrowsRotate,
    color: 'blue.solid',
  },
  [LearnerFeedbackAttributeTag.AskedGoodQuestions]: {
    label: 'Asked good questions',
    icon: faCircleQuestion,
    color: 'blue.solid',
  },
  [LearnerFeedbackAttributeTag.HelpedGuideDiscussion]: {
    label: 'Helped guide discussion',
    icon: faCompass,
    color: 'blue.solid',
  },
  [LearnerFeedbackAttributeTag.NuancedPerspective]: {
    label: 'Nuanced perspective',
    icon: faEye,
    color: 'blue.solid',
  },
  [LearnerFeedbackAttributeTag.GoodListener]: {
    label: 'Good listener',
    icon: faEarListen,
    color: 'blue.solid',
  },
  [LearnerFeedbackAttributeTag.Knowledgeable]: {
    label: 'Knowledgeable',
    icon: faGraduationCap,
    color: 'blue.solid',
  },
  [LearnerFeedbackAttributeTag.WellSpoken]: {
    label: 'Well spoken',
    icon: faMicrophone,
    color: 'blue.solid',
  },
  [LearnerFeedbackAttributeTag.Logical]: {
    label: 'Logical',
    icon: faBrain,
    color: 'blue.solid',
  },
  [LearnerFeedbackAttributeTag.Positive]: {
    label: 'Positive',
    icon: faSun,
    color: 'blue.solid',
  },
  [LearnerFeedbackAttributeTag.Passionate]: {
    label: 'Passionate',
    icon: faFire,
    color: 'blue.solid',
  },
  [LearnerFeedbackAttributeTag.Authentic]: {
    label: 'Authentic',
    icon: faFingerprint,
    color: 'blue.solid',
  },
  [LearnerFeedbackAttributeTag.Calm]: {
    label: 'Calm',
    icon: faWater,
    color: 'blue.solid',
  },
}
